import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AsyncSection } from '@/app/components/AsyncSection';
import { SubmitError } from '@/app/components/SubmitError';
import { Badge } from '@/app/components/ui/Badge';
import { Card, CardHeader } from '@/app/components/ui/Card';
import { Field, FormActions, Input } from '@/app/components/ui/Field';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { useActiveCompany } from '@/features/company/CompanyContext';
import { useMe } from '@/features/me/MeContext';
import { getCompany, updateCompany } from '@/features/settings/api';
import { useAsync } from '@/lib/useAsync';
import { useSubmit } from '@/lib/useSubmit';
import { Button } from '@/app/components/ui/Button';

/**
 * `/settings` — Instellingen › Profiel (§ WP-08): organisatienaam and
 * branche, editable by a company admin (`companies.update`); aantal
 * medewerkers (afgeleid) and het plan are read-only here — plan comes from
 * Abonnement (Fase 1 WP-10), not repeated as its own form field.
 */
export function SettingsProfilePage() {
  const { t } = useTranslation();
  const { company } = useActiveCompany();
  const { permissions } = useMe();
  const companyId = company ? company.id : null;

  const state = useAsync(
    () => (companyId ? getCompany(companyId) : Promise.reject(new Error('no company'))),
    [companyId],
  );

  const [name, setName] = useState<string | null>(null);
  const [industry, setIndustry] = useState<string | null>(null);
  const form = useSubmit(async (currentName: string, currentIndustry: string) => {
    if (!companyId) return;
    await updateCompany(companyId, {
      name: currentName,
      industry: currentIndustry === '' ? null : currentIndustry,
    });
    state.reload();
  });

  const canEdit = companyId !== null && permissions.canUpdateCompany(companyId);

  return (
    <section className="flex flex-col gap-6">
      <PageHeader title={t('settings.title')} lead={t('settings.lead')} />

      <Card>
        <CardHeader title={t('settings.profile.title')} />
        {!companyId && <p className="text-accent text-sm">{t('settings.noCompany')}</p>}
        {companyId && (
          <AsyncSection state={state} label={t('settings.profile.title')} skeleton="card">
            {(data) => {
              const currentName = name ?? data.name ?? '';
              const currentIndustry = industry ?? data.industry ?? '';
              return (
                <form
                  className="flex flex-col gap-4"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void form.submit(currentName, currentIndustry);
                  }}
                >
                  <Field label={t('settings.profile.name')}>
                    <Input
                      value={currentName}
                      onChange={(e) => setName(e.target.value)}
                      disabled={!canEdit}
                      required
                    />
                  </Field>
                  <Field
                    label={t('settings.profile.industry')}
                    hint={t('settings.profile.industryHint')}
                  >
                    <Input
                      value={currentIndustry}
                      onChange={(e) => setIndustry(e.target.value)}
                      disabled={!canEdit}
                    />
                  </Field>
                  <Field label={t('settings.profile.employeeCount')}>
                    <Input value={String(data.employee_count ?? 0)} disabled readOnly />
                  </Field>
                  <Field label={t('settings.profile.plan')}>
                    <Badge tone="outline" className="self-start">
                      {t('settings.profile.planPlaceholder')}
                    </Badge>
                  </Field>
                  {canEdit && (
                    <>
                      <SubmitError error={form.error} />
                      <FormActions>
                        <Button type="submit" disabled={form.pending}>
                          {t('settings.save')}
                        </Button>
                      </FormActions>
                    </>
                  )}
                </form>
              );
            }}
          </AsyncSection>
        )}
      </Card>
    </section>
  );
}
